
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const EyeIcon = ({ show }: { show: boolean }) => (
  <svg
    className="w-5 h-5 text-luxury-cream-300 hover:text-luxury-gold-400 transition-colors duration-200"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    {show ? (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </>
    ) : (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
      </>
    )}
  </svg>
)

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const router = useNavigate()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError('') // Clear error when user types
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Store token and user info in localStorage
        localStorage.setItem('fga_admin_token', data.token)
        localStorage.setItem('fga_admin_user', JSON.stringify({
          ...data.user,
          loginTime: new Date().toISOString()
        }))

        // Redirect to admin dashboard
        window.location.href = '/admin'
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (error) {
      setError('Login failed. Please try again.')
    }

    setIsLoading(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-luxury-black-950 p-4">
      <div className="w-full max-w-md">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <h1 className="luxury-heading text-3xl text-luxury-gold-400 mb-2">
            FGA Prestige
          </h1>
          <p className="text-luxury-cream-200 text-sm uppercase tracking-wider">
            Admin Portal
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-luxury-black-900 p-8 shadow-luxury border border-luxury-gold-500/20">
          <h2 className="luxury-subheading text-xl text-white mb-6 text-center">
            Content Management System
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                placeholder="Username"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 pr-12 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  tabIndex={-1}
                >
                  <EyeIcon show={showPassword} />
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-3 text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`
                w-full py-3 px-4 font-medium uppercase tracking-wider transition-all duration-300
                ${isLoading 
                  ? 'bg-luxury-gold-500/50 text-luxury-black-700 cursor-not-allowed' 
                  : 'bg-luxury-gold-500 hover:bg-luxury-gold-400 text-luxury-black-950'
                }
              `}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-luxury-cream-300">
            © 2024 FGA Prestige. All rights reserved.
          </p>
          <div className="mt-4">
            <a
              href="/"
              className="text-luxury-gold-400 hover:text-luxury-gold-300 text-sm font-medium uppercase tracking-wider transition-colors duration-300"
            >
              ← Back to Website
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin